import { useInfiniteQuery } from "@tanstack/react-query";
import { invoiceLink, type InvoiceStatus } from "../../lib/api";
import { invoicesQuery } from "../../lib/queries";
import { LinkButton, SecondaryButton } from "../ui/button";
import { statedDate } from "./plan";

export const INVOICES_TITLE = "Invoices and receipts";
export const INVOICES_FAILED = "Your invoices couldn't be loaded.";
export const INVOICES_RETRY = "Try again";
export const INVOICES_MORE = "Show older";
export const VIEW_INVOICE = "View";
export const INVOICE_PDF = "PDF";

/** Where an invoice stands, in a person's words. */
export const INVOICE_STATUS: Record<InvoiceStatus, string> = {
  paid: "Paid",
  due: "Due",
  failed: "Payment failed",
  refunded: "Refunded",
  partly_refunded: "Partly refunded",
};

/**
 * THE INVOICES AND RECEIPTS (wave 87) — one view over from the panel's index,
 * as the ladder is. Ours, not the processor's portal: the portal's own invoice
 * history is off, so this is the one list.
 *
 * Newest first, 24 at a time. Each row: the date (paid, or finalized where it
 * is unpaid), what it was for, the total with its tax, where it stands, and
 * two links that go THROUGH THE SERVER — it checks the invoice is yours and
 * reads the processor's link fresh — opened in a new tab: "View", the hosted
 * invoice, where the processor offers the invoice and the receipt for
 * download, and "PDF".
 *
 * Dollars are allowed here: this is billing. No capacity or allowance figure
 * appears anywhere in it. There is no empty state, because the row that opens
 * this view is hidden until an invoice exists.
 */
export function InvoicesView() {
  const q = useInfiniteQuery({ ...invoicesQuery(), retry: false });

  if (q.isError) {
    return (
      <div className="px-6 py-8 text-center" role="alert">
        <p className="text-sm text-text-soft">{INVOICES_FAILED}</p>
        <SecondaryButton
          type="button"
          className="mt-3 h-9 px-3 text-xs"
          onClick={() => void q.refetch()}
        >
          {INVOICES_RETRY}
        </SecondaryButton>
      </div>
    );
  }
  if (!q.data) return null;
  const rows = q.data.pages.flatMap((p) => p.invoices);

  return (
    <div className="px-3 pt-2 pb-8">
      <ul data-paper className="overflow-hidden rounded-md bg-bg" aria-label={INVOICES_TITLE}>
        {rows.map((row) => (
          <li
            key={row.id}
            data-invoice={row.id}
            className="border-b border-border-soft px-3 py-3 last:border-b-0"
          >
            <div className="flex items-baseline justify-between gap-3">
              <p className="min-w-0 text-[13px] font-medium text-text">{statedDate(row.date)}</p>
              <p className="shrink-0 text-[13px] font-medium tabular-nums text-text">
                {row.total_display}
              </p>
            </div>
            <div className="mt-0.5 flex items-baseline justify-between gap-3">
              <p className="min-w-0 text-[12px] leading-snug text-muted">{row.description}</p>
              <p
                className={`shrink-0 text-[12px] ${
                  row.status === "failed"
                    ? "text-danger"
                    : row.status === "due"
                      ? "text-warn"
                      : "text-faint"
                }`}
              >
                {INVOICE_STATUS[row.status]}
              </p>
            </div>
            <div className="mt-1.5 flex gap-4 text-[12px]">
              <a
                href={invoiceLink(row.id, "page")}
                target="_blank"
                rel="noreferrer"
                className="font-medium text-text underline underline-offset-4"
              >
                {VIEW_INVOICE}
              </a>
              <a
                href={invoiceLink(row.id, "pdf")}
                target="_blank"
                rel="noreferrer"
                className="font-medium text-text underline underline-offset-4"
              >
                {INVOICE_PDF}
              </a>
            </div>
          </li>
        ))}
      </ul>
      {q.hasNextPage && (
        <div className="pt-4 text-center">
          <LinkButton
            type="button"
            disabled={q.isFetchingNextPage}
            onClick={() => void q.fetchNextPage()}
          >
            {INVOICES_MORE}
          </LinkButton>
        </div>
      )}
    </div>
  );
}
